import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AppBar, Box, Toolbar, Typography, Drawer, List, ListItemButton, ListItemText, ListItemIcon,
  Divider, IconButton, Avatar, Menu, MenuItem, useMediaQuery, Tooltip, Chip, Collapse,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  People, Security, Business, Folder, Place, Dashboard, Handshake, Engineering, Badge, Inventory2,
  PriceChange, Hub, Category, Description, RequestQuote, ReceiptLong, ShoppingCart, Assignment, UploadFile, SwapHoriz,
  LocalShipping, Insights, Menu as MenuIcon, MenuOpen, Logout, Bolt, EngineeringOutlined,
  AccountBalance, ReceiptOutlined, Payments, RuleFolder, MarkEmailUnread, Mail, NotificationsActive, FactCheck,
  QueryStats, Assessment, TrackChanges, AccountTree, ListAlt,
  Warehouse, Receipt, QrCode2, PhoneIphone,
  EvStation, Verified, ConfirmationNumber, SupportAgent, Insights as InsightsIcon,
  Dns, Apartment, Loyalty, Storefront, AccountBalanceWallet, VpnKey, Webhook, ErrorOutline, MonitorHeart, MenuBook, Layers, Groups, Factory, AutoAwesome,
  ExpandLess, ExpandMore,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useVertical } from '../../hooks/useVertical';
import { brandingApi } from '../../api/platform.api';
import { ColorModeToggle } from '../ColorModeToggle';
import { NotificationMenu } from '../NotificationMenu';
import { VerticalSwitcher } from './VerticalSwitcher';
import { Breadcrumbs } from '../Breadcrumbs';
import { ChangePasswordDialog } from '../ChangePasswordDialog';
import { ProfileCompletionGate } from '../ProfileCompletionGate';

const DRAWER_WIDTH = 240;
const DRAWER_MINI = 72;

interface NavItem { label: string; path: string; icon: ReactNode; permission?: string }
/**
 * `verticals` tags a section to one or more business verticals (by code).
 * When a vertical is pinned, the menu becomes that vertical's mini-CRM:
 *   - vertical-tagged section matching the pin  → shown
 *   - vertical-tagged section not matching      → hidden
 *   - untagged platform section                 → hidden, UNLESS `alwaysShow`
 *   - untagged + `alwaysShow` (Overview/Admin)   → always shown
 * With no pin ("All Verticals"), every section shows.
 */
interface NavSection { heading: string; items: NavItem[]; verticals?: string[]; alwaysShow?: boolean; excludeVerticals?: string[] }

const NAV: NavSection[] = [
  {
    // Global landing — shown only in "All Verticals" (admin/unrestricted). When a
    // specific vertical is active, that vertical's own Home group supplies the
    // dashboard / approvals / notifications, so Overview is hidden to avoid duplication.
    heading: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
      { label: 'My Approvals', path: '/my-approvals', icon: <FactCheck />, permission: 'approval.read' },
      { label: 'Notifications', path: '/notifications', icon: <NotificationsActive /> },
    ],
  },
  // ===== EPC vertical — core groups pinned to the top: Masters → Vendor Lifecycle → EPC =====
  {
    heading: 'Masters',
    // EPC construction masters (clients, projects, sites, technicians, employees, materials, labour rates).
    verticals: ['epc'],
    items: [
      // Client Master is managed through the Client Onboarding lifecycle (single source of
      // truth). The /clients route remains for deep links and other modules' references.
      { label: 'Client Onboarding', path: '/client-applications', icon: <Business />, permission: 'client_onboarding.manage' },
      { label: 'Client Dashboard', path: '/client-onboarding-dashboard', icon: <Insights />, permission: 'client_onboarding.manage' },
      // Project Master is managed through the Project Onboarding lifecycle (single source of
      // truth). The /projects route remains for deep links and other modules' references.
      { label: 'Project Onboarding', path: '/project-applications', icon: <Folder />, permission: 'project_onboarding.manage' },
      { label: 'Project Dashboard', path: '/project-onboarding-dashboard', icon: <Insights />, permission: 'project_onboarding.manage' },
      // Site Master is managed through the Site Onboarding lifecycle (single source of
      // truth). The /sites route remains for deep links and other modules' references.
      { label: 'Site Onboarding', path: '/site-applications', icon: <Place />, permission: 'site_onboarding.manage' },
      { label: 'Site Dashboard', path: '/site-onboarding-dashboard', icon: <Insights />, permission: 'site_onboarding.manage' },
      // Vendor Master is no longer surfaced directly — vendors are managed through the
      // Vendor Lifecycle section (single source of truth). The /vendors route remains for
      // deep links and other modules' references.
      // Technician Master is managed through the Technician Onboarding lifecycle (single
      // source of truth). The /technicians route remains for deep links + references.
      { label: 'Technician Onboarding', path: '/technician-applications', icon: <Engineering />, permission: 'technician_onboarding.manage' },
      { label: 'Technician Dashboard', path: '/technician-onboarding-dashboard', icon: <Insights />, permission: 'technician_onboarding.manage' },
      { label: 'Employees', path: '/employees', icon: <Badge />, permission: 'employee.read' },
      { label: 'Materials', path: '/materials', icon: <Inventory2 />, permission: 'material.read' },
      { label: 'Labour Rates', path: '/labour-rates', icon: <PriceChange />, permission: 'labour_rate.read' },
    ],
  },
  {
    heading: 'Vendor Lifecycle',
    // EPC vendor onboarding / document-verification.
    verticals: ['epc'],
    items: [
      { label: 'Vendor Onboarding', path: '/vendor-portal/onboarding', icon: <Handshake />, permission: 'vendor_onboarding.submit' },
      { label: 'Vendor Applications', path: '/vendor-applications', icon: <FactCheck />, permission: 'vendor_onboarding.manage' },
      { label: 'Onboarding Dashboard', path: '/vendor-onboarding-dashboard', icon: <Insights />, permission: 'vendor_onboarding.manage' },
    ],
  },
  {
    heading: 'Site Execution',
    verticals: ['epc'],
    items: [
      { label: 'Dashboard', path: '/epc/dashboard', icon: <Dashboard />, permission: 'epc_milestone.read' },
      { label: 'Site Monitoring', path: '/epc/site-monitoring', icon: <AccountTree />, permission: 'epc_site_monitoring.read' },
      { label: 'DISCOM Applications', path: '/epc/discom', icon: <Bolt />, permission: 'am_discom.read' },
      { label: 'BOQ', path: '/epc/boq', icon: <ReceiptLong />, permission: 'epc_boq.read' },
      { label: 'Requisition Request', path: '/epc/requisitions', icon: <ListAlt />, permission: 'epc_requisition.read' },
      { label: 'Local Purchase Order', path: '/epc/purchase-orders', icon: <ShoppingCart />, permission: 'epc_purchase_order.read' },
      { label: 'Work Orders', path: '/epc/work-orders', icon: <Engineering />, permission: 'epc_work_order.read' },
      { label: 'Milestones', path: '/epc/milestones', icon: <AccountTree />, permission: 'epc_milestone.read' },
      { label: 'Invoices', path: '/epc/invoices', icon: <RequestQuote />, permission: 'epc_invoice.read' },
      { label: 'Payments', path: '/epc/payments', icon: <Payments />, permission: 'epc_payment.read' },
      { label: 'Handovers', path: '/epc/handovers', icon: <Assignment />, permission: 'epc_handover.read' },
      { label: 'Closure', path: '/epc/closures', icon: <FactCheck />, permission: 'epc_closure.read' },
    ],
  },
  // Shared Operations (OMS / BOQ / Work Orders) intentionally removed from EPC — EPC has its own
  // EPC BOQ + EPC Work Orders and uses Site Monitoring instead of the shared OMS.
  {
    heading: 'Inventory & Assets',
    // Warehouse / stock — used by EPC and PDI.
    verticals: ['epc', 'protection_device_installation'],
    items: [
      { label: 'Inventory Dashboard', path: '/inventory', icon: <Inventory2 />, permission: 'inventory.read' },
      { label: 'Procurement Review', path: '/epc/procurement-review', icon: <FactCheck />, permission: 'epc_purchase_order.create' },
      { label: 'RFQ', path: '/epc/rfq', icon: <RequestQuote />, permission: 'epc_requisition.read' },
      { label: 'Purchase Orders', path: '/epc/rfq-purchase-orders', icon: <ShoppingCart />, permission: 'epc_purchase_order.read' },
      { label: 'Purchases', path: '/inventory-purchases', icon: <ReceiptLong />, permission: 'inventory.transact' },
      { label: 'Warehouses', path: '/warehouses', icon: <Warehouse />, permission: 'warehouse.read' },
      { label: 'Item Master', path: '/inventory-items', icon: <Category />, permission: 'inventory_item.read' },
      { label: 'Stock Ledger', path: '/stock-ledger', icon: <Receipt />, permission: 'inventory.read' },
      { label: 'Asset Tracking', path: '/asset-tracking', icon: <QrCode2 />, permission: 'inventory.read' },
    ],
  },
  {
    // Standalone (not vertical-gated) so external client approvers — who aren't assigned a
    // vertical — can still reach their payment approvals. Gated only by am_payment.read.
    // Hidden in EPC (EPC has its own Payment Approvals section below).
    heading: 'Approvals',
    alwaysShow: true,
    excludeVerticals: ['epc'],
    items: [
      { label: 'Payment Approvals', path: '/asset-management/payments', icon: <ReceiptLong />, permission: 'am_payment.read' },
    ],
  },
  {
    // EPC payment approvals — its own Approvals group in the EPC mini-CRM.
    heading: 'Approvals',
    verticals: ['epc'],
    items: [
      { label: 'Payment Approvals', path: '/epc/payment-approvals', icon: <FactCheck />, permission: 'epc_payment.approve' },
    ],
  },
  {
    heading: 'Finance',
    // Cross-vertical — shown in every vertical (permission-gated).
    alwaysShow: true,
    items: [
      { label: 'Finance Dashboard', path: '/finance', icon: <AccountBalance />, permission: 'payment.read' },
      { label: 'Invoices', path: '/invoices', icon: <ReceiptOutlined />, permission: 'invoice.read' },
      { label: 'Payments', path: '/payments', icon: <Payments />, permission: 'payment.read' },
    ],
  },
  {
    heading: 'Business Intelligence',
    // Cross-vertical — shown in every vertical (permission-gated).
    alwaysShow: true,
    items: [
      { label: 'Profitability', path: '/profitability', icon: <QueryStats />, permission: 'profitability.read' },
      { label: 'P&L Statements', path: '/pnl', icon: <Assessment />, permission: 'profitability.read' },
      { label: 'Targets', path: '/targets', icon: <TrackChanges />, permission: 'target.read' },
      { label: 'Revenue & Cost', path: '/profit-entries', icon: <AccountTree />, permission: 'cost.read' },
    ],
  },
  {
    heading: 'Field Force',
    // Cross-vertical — shown in every vertical (permission-gated).
    alwaysShow: true,
    items: [
      { label: 'Mobile Preview', path: '/field', icon: <PhoneIphone />, permission: 'field.read' },
    ],
  },
  {
    heading: 'Service & AMC',
    verticals: ['amc', 'repair_maintenance'],
    items: [
      { label: 'Service Analytics', path: '/service-analytics', icon: <InsightsIcon />, permission: 'amc.read' },
      { label: 'Asset Registry', path: '/service-assets', icon: <EvStation />, permission: 'service_asset.read' },
      { label: 'AMC Contracts', path: '/amc-contracts', icon: <Verified />, permission: 'amc_contract.read' },
      { label: 'Service Tickets', path: '/service-tickets', icon: <ConfirmationNumber />, permission: 'ticket.read' },
      { label: 'Customer Portal', path: '/customer-portal', icon: <SupportAgent />, permission: 'amc.read' },
      // EVCPD service, AMC & PM (moved here from the EVCPD section).
      { label: 'Service Tickets', path: '/evcpd/service-tickets', icon: <ConfirmationNumber />, permission: 'service_ticket.read' },
      { label: 'Service Dashboard', path: '/evcpd/service-dashboard', icon: <Insights />, permission: 'service_ticket.read' },
      { label: 'AMC Contracts', path: '/evcpd/amc', icon: <Verified />, permission: 'evcpd_amc_contract.read' },
      { label: 'AMC Dashboard', path: '/evcpd/amc-dashboard', icon: <Insights />, permission: 'evcpd_amc_contract.read' },
      { label: 'AMC Reports', path: '/evcpd/amc-reports', icon: <Assessment />, permission: 'evcpd_amc_contract.read' },
      { label: 'PM Execution', path: '/evcpd/pm-execution', icon: <EngineeringOutlined />, permission: 'evcpd_pm.execute' },
      { label: 'PM Checklists', path: '/evcpd/checklist-templates', icon: <RuleFolder />, permission: 'evcpd_checklist.read' },
    ],
  },
  // ===== Protection Device Installation vertical — spec-aligned menu structure =====
  // HOME (Dashboard) and NOTIFICATIONS are served by the global "Overview" group above.
  // ADMINISTRATION is served by the global "Administration" group below.
  // FULFILMENT (Dispatch/Delivery), SOURCING (Manufacturers) and ONBOARDING (Dealer
  // Registration) are pending dedicated pages (later phases) and are intentionally
  // not yet listed to avoid dead links.
  {
    heading: 'Home',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Dashboard', path: '/pdi/home', icon: <Dashboard />, permission: 'dealer_order.read' },
      { label: 'Deep Analytics', path: '/pdi/analytics', icon: <QueryStats />, permission: 'dealer_order.read' },
      { label: 'My Approvals', path: '/my-approvals', icon: <FactCheck />, permission: 'approval.read' },
      { label: 'Notifications Center', path: '/notifications', icon: <NotificationsActive /> },
    ],
  },
  {
    heading: 'Orders',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Place Order', path: '/evcpd/orders/place', icon: <ShoppingCart />, permission: 'dealer_order.create' },
      { label: 'My Orders', path: '/evcpd/orders', icon: <ReceiptLong />, permission: 'dealer_order.read' },
      { label: 'Order Confirmation', path: '/evcpd/orders/confirm', icon: <FactCheck />, permission: 'dealer_order.read' },
    ],
  },
  {
    heading: 'Fulfilment',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Dispatch', path: '/fulfilment/dispatch', icon: <LocalShipping />, permission: 'dealer_dispatch.create' },
      { label: 'Delivery', path: '/fulfilment/delivery', icon: <Inventory2 />, permission: 'dealer_dispatch.deliver' },
    ],
  },
  {
    heading: 'Finance',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Reconciliation', path: '/evcpd/dealer-payments', icon: <QueryStats />, permission: 'dealer_payment.read' },
      { label: 'Ageing Report', path: '/pdi/ageing', icon: <ReceiptLong />, permission: 'dealer_payment.read' },
      { label: 'Dealer Payments', path: '/pdi/dealer-finance', icon: <AccountBalanceWallet />, permission: 'dealer_payment.read' },
    ],
  },
  {
    heading: 'Masters',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Product Master', path: '/evcpd/products', icon: <Category />, permission: 'evcpd_product.read' },
      { label: 'Dealer Master', path: '/evcpd/dealers', icon: <Storefront />, permission: 'dealer.read' },
    ],
  },
  {
    heading: 'Sourcing',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Manufacturers', path: '/sourcing/manufacturers', icon: <Factory />, permission: 'manufacturer.read' },
      { label: 'Manufacturing Orders', path: '/sourcing/manufacturing-orders', icon: <ShoppingCart />, permission: 'manufacturing_order.read' },
      { label: 'Daily Commitment', path: '/sourcing/daily-commitment', icon: <Engineering />, permission: 'manufacturing_order.read' },
      { label: 'Manufacturer Payments', path: '/sourcing/payments', icon: <AccountBalanceWallet />, permission: 'manufacturer_payment.read' },
      { label: 'Onboard Manufacturer', path: '/sourcing/manufacturers/onboard', icon: <Handshake />, permission: 'manufacturer.create' },
    ],
  },
  {
    heading: 'Clients',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Client Master', path: '/client-applications', icon: <Business />, permission: 'client_onboarding.manage' },
      { label: 'Client–Dealer Mapping', path: '/pdi/client-mappings', icon: <Category />, permission: 'client_product.manage' },
    ],
  },
  {
    heading: 'Onboarding',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Dealer Registration', path: '/pdi/dealer-applications', icon: <Handshake />, permission: 'dealer_application.read' },
    ],
  },
  {
    // Dashboards consolidated into Home → Dashboard + Deep Analytics. Insights keeps
    // only the action screens (mapping + targets); Team Performance lives in Administration.
    heading: 'Insights',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Dealer Mapping', path: '/insights/dealer-mapping', icon: <Storefront />, permission: 'dealer_insight.read' },
      { label: 'Targets', path: '/insights/dealer-targets', icon: <TrackChanges />, permission: 'dealer_insight.read' },
    ],
  },
  {
    // Off-spec but preserved (user choice): installation execution + manufacturing/production.
    heading: 'Operations',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'Installations', path: '/evcpd/installations', icon: <EngineeringOutlined />, permission: 'installation.read' },
      { label: 'Production Plans', path: '/evcpd/production', icon: <Engineering />, permission: 'production_plan.read' },
      { label: 'Stock Handovers', path: '/evcpd/stock-handovers', icon: <Warehouse />, permission: 'stock_handover.read' },
    ],
  },
  {
    heading: 'Client Portal',
    verticals: ['protection_device_installation'],
    items: [
      { label: 'My Dashboard', path: '/pdi/my-dashboard', icon: <Insights />, permission: 'client_portal.read' },
      { label: 'My Orders', path: '/pdi/my-orders', icon: <ShoppingCart />, permission: 'dealer_order.read' },
      { label: 'My Installations', path: '/evcpd/installations', icon: <EngineeringOutlined />, permission: 'installation.read' },
      { label: 'My Service Tickets', path: '/evcpd/service-tickets', icon: <ConfirmationNumber />, permission: 'service_ticket.read' },
      { label: 'My Finance', path: '/pdi/my-finance', icon: <AccountBalanceWallet />, permission: 'client_portal.read' },
    ],
  },
  {
    heading: 'Asset Management',
    verticals: ['asset_management'],
    items: [
      { label: 'Dashboard', path: '/asset-management/dashboard', icon: <Dashboard />, permission: 'am_station.read' },
      { label: 'Deep Analytics', path: '/asset-management/analytics', icon: <QueryStats />, permission: 'am_station.read' },
      { label: 'Site Registration', path: '/asset-management/stations', icon: <EvStation />, permission: 'am_station.read' },
      { label: 'Rent Service', path: '/asset-management/rent', icon: <Payments />, permission: 'am_rent.read' },
      { label: 'Agreements', path: '/asset-management/agreements', icon: <Description />, permission: 'am_agreement.read' },
      { label: 'Electricity Bills', path: '/asset-management/electricity', icon: <Bolt />, permission: 'am_electricity.read' },
      { label: 'Charger CMS', path: '/asset-management/cms', icon: <EvStation />, permission: 'am_electricity.read' },
      { label: 'CMS Connections', path: '/asset-management/cms-connections', icon: <Hub />, permission: 'am_cms.read' },
      { label: 'Asset Register', path: '/asset-management/assets', icon: <QrCode2 />, permission: 'am_asset.read' },
      { label: 'Asset Transfers', path: '/asset-management/transfers', icon: <SwapHoriz />, permission: 'am_asset.read' },
    ],
  },
  {
    heading: 'Repair & Maintenance',
    verticals: ['repair_maintenance'],
    items: [
      { label: 'Complaints', path: '/repair-maintenance/complaints', icon: <SupportAgent />, permission: 'rm_complaint.read' },
    ],
  },
  {
    heading: 'BOQ Library',
    // EPC construction BOQ inputs (categories, SOW, templates, rate cards).
    verticals: ['epc'],
    items: [
      { label: 'BOQ Categories', path: '/boq-categories', icon: <Category />, permission: 'category.read' },
      { label: 'SOW Categories', path: '/sow-categories', icon: <Category />, permission: 'category.read' },
      { label: 'Templates', path: '/boq-templates', icon: <Description />, permission: 'boq_template.read' },
      { label: 'Rate Cards', path: '/rate-cards', icon: <RequestQuote />, permission: 'rate_card.read' },
      { label: 'Upload BOQ', path: '/boq-upload', icon: <UploadFile />, permission: 'rate_card.create' },
      { label: 'Manufacturer Rates', path: '/manufacturer-price-lists', icon: <PriceChange />, permission: 'rate_card.read' },
      { label: 'Purchase Rates', path: '/boq-purchases', icon: <ReceiptLong />, permission: 'rate_card.read' },
      { label: 'BOQ Rate Lookup', path: '/rate-lookup', icon: <RequestQuote />, permission: 'rate_card.read' },
    ],
  },
  {
    heading: 'Automation',
    items: [
      { label: 'Approval Matrix', path: '/approval-matrix', icon: <RuleFolder />, permission: 'approval_matrix.read' },
      { label: 'Email Templates', path: '/email-templates', icon: <Mail />, permission: 'email_template.read' },
      { label: 'Email Logs', path: '/email-logs', icon: <MarkEmailUnread />, permission: 'email.read' },
      { label: 'Email Settings', path: '/email-settings', icon: <Mail />, permission: 'email.manage' },
      { label: 'WhatsApp Hub', path: '/whatsapp', icon: <Hub />, permission: 'whatsapp.read' },
      { label: 'WhatsApp Templates', path: '/whatsapp-templates', icon: <Hub />, permission: 'whatsapp_template.read' },
      { label: 'WhatsApp Settings', path: '/whatsapp-settings', icon: <Hub />, permission: 'whatsapp.manage' },
    ],
  },
  {
    heading: 'Integration Hub',
    items: [
      { label: 'Integration Dashboard', path: '/integrations/dashboard', icon: <Insights />, permission: 'integration.read' },
      { label: 'Marketplace', path: '/integrations/marketplace', icon: <Storefront />, permission: 'integration.read' },
      { label: 'Integrations', path: '/integrations', icon: <Hub />, permission: 'integration.read' },
      { label: 'API Keys', path: '/integrations/api-keys', icon: <VpnKey />, permission: 'integration_key.read' },
      { label: 'Webhooks', path: '/integrations/webhooks', icon: <Webhook />, permission: 'webhook.read' },
      { label: 'Failed Queue', path: '/integrations/failed-queue', icon: <ErrorOutline />, permission: 'integration_log.read' },
      { label: 'Monitoring', path: '/integrations/monitoring', icon: <MonitorHeart />, permission: 'integration_log.read' },
      { label: 'API Audit Logs', path: '/integrations/logs', icon: <Assessment />, permission: 'integration_log.read' },
      { label: 'API Docs', path: '/integrations/docs', icon: <MenuBook />, permission: 'integration.read' },
    ],
  },
  {
    heading: 'Administration',
    alwaysShow: true,
    items: [
      { label: 'Users', path: '/users', icon: <People />, permission: 'user.read' },
      { label: 'Roles & Permissions', path: '/roles', icon: <Security />, permission: 'role.read' },
      { label: 'Vertical Access', path: '/admin/vertical-access', icon: <Layers />, permission: 'business_vertical.manage' },
      { label: 'Team Performance', path: '/admin/team-performance', icon: <Groups />, permission: 'business_vertical.read' },
      { label: 'Vertical Go-Live', path: '/admin/vertical-go-live', icon: <Bolt />, permission: 'business_vertical.golive' },
      { label: 'Security Dashboard', path: '/security', icon: <Security />, permission: 'security.read' },
      { label: 'Security Settings', path: '/security-settings', icon: <Security />, permission: 'security.manage' },
      { label: 'Document AI', path: '/ai-settings', icon: <AutoAwesome />, permission: 'integration.manage' },
    ],
  },
  {
    heading: 'Platform Administration',
    alwaysShow: true,
    items: [
      { label: 'Platform Dashboard', path: '/platform', icon: <Dns />, permission: 'platform.read' },
      { label: 'Tenant Management', path: '/platform/tenants', icon: <Apartment />, permission: 'platform.read' },
      { label: 'Plans', path: '/platform/plans', icon: <Loyalty />, permission: 'platform.read' },
    ],
  },
];

function initials(name?: string): string {
  if (!name) return 'U';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
}

export function AppLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout, hasPermission } = useAuth();
  const { activeVertical, verticals } = useVertical();
  // Codes of the LIVE (active, accessible) verticals — drives phased go-live:
  // a vertical-tagged menu section is shown only if at least one of its verticals
  // is live. Combined with the per-pin filter below, an un-launched vertical's
  // menu stays hidden even in "All Verticals" mode.
  const liveVerticalCodes = new Set(verticals.map((v) => v.code));
  // The vertical whose mini-CRM the menu should show. Either the explicit pin, or
  // — for a user RESTRICTED to a single vertical (e.g. a Business Head) — that one
  // vertical automatically (they have no switcher to pin). Unrestricted users
  // (admin/super, accessibleVerticalIds === null) are NOT auto-scoped: they see the
  // whole platform until they pin a vertical.
  const verticalRestricted = Array.isArray(user?.accessibleVerticalIds);
  const effectiveVertical = activeVertical ?? (verticalRestricted && verticals.length === 1 ? verticals[0] : null);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [pwOpen, setPwOpen] = useState(false);
  // Collapsible nav groups (accordion). A group is open if explicitly toggled,
  // else defaults to open when it contains the active route.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (h: string) => setOpenGroups((g) => ({ ...g, [h]: !(g[h] ?? false) }));
  const sectionHasActive = (items: NavItem[]) => items.some((i) => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`));

  // PORTAL nav tailoring (dealer & client): their Overview leads with their vertical's home
  // dashboard + deep analytics + notifications center (promoted from the vertical group), and the
  // generic cross-module Dashboard is dropped. Staff/admin nav is unchanged. A portal user is
  // identified by their linked entity (set at provisioning), NOT by a role code — so custom roles
  // (e.g. a per-client "Demo Client" role) work too.
  const portalType = user?.linkedEntityType; // 'client' | 'dealer' | ...
  const isPortal = portalType === 'client' || portalType === 'dealer';
  // A client is an Asset Management client if it has AM access; otherwise it's treated as a PDI client.
  const clientHasAm = hasPermission('am_station.read');
  const navSections = useMemo<NavSection[]>(() => {
    if (!isPortal) return NAV;
    if (portalType === 'dealer') {
      // PDI dealer portal.
      const overviewItems: NavItem[] = [
        { label: 'Dashboard', path: '/pdi/home', icon: <Dashboard />, permission: 'dealer_order.read' },
        { label: 'Deep Analytics', path: '/pdi/analytics', icon: <QueryStats />, permission: 'dealer_order.read' },
        { label: 'Notifications Center', path: '/notifications', icon: <NotificationsActive /> },
      ];
      return NAV
        // Dealers don't manage clients — hide the Clients section from their panel entirely.
        .filter((section) => section.heading !== 'Clients')
        .map((section) => {
          if (section.heading === 'Overview') return { ...section, items: overviewItems };
          // Strip the items promoted into Overview from the PDI Home group (leaves e.g. My Approvals).
          if (section.heading === 'Home') return { ...section, items: section.items.filter((i) => !['/pdi/home', '/pdi/analytics', '/notifications'].includes(i.path)) };
          return section;
        });
    }
    // PDI client portal — a focused, fixed nav (only these items, nothing else).
    if (!clientHasAm) {
      return [{
        heading: 'Overview',
        alwaysShow: true,
        items: [
          { label: 'Dashboard', path: '/pdi/my-dashboard', icon: <Dashboard />, permission: 'client_portal.read' },
          { label: 'Deep Analytics', path: '/pdi/analytics', icon: <QueryStats />, permission: 'dealer_order.read' },
          { label: 'Place Order', path: '/evcpd/orders/place', icon: <ShoppingCart />, permission: 'dealer_order.create' },
          { label: 'My Orders', path: '/evcpd/orders', icon: <ReceiptLong />, permission: 'dealer_order.read' },
          { label: 'Order Confirmation', path: '/evcpd/orders/confirm', icon: <FactCheck />, permission: 'dealer_order.read' },
          { label: 'Product Master', path: '/evcpd/products', icon: <Category />, permission: 'evcpd_product.read' },
          { label: 'My Finance', path: '/pdi/my-finance', icon: <AccountBalanceWallet />, permission: 'client_portal.read' },
          { label: 'Notifications', path: '/notifications', icon: <NotificationsActive /> },
        ],
      }];
    }
    // Asset Management client portal — Overview leads with the AM dashboard + deep analytics.
    const amDashboard: NavItem = { label: 'Dashboard', path: '/asset-management/dashboard', icon: <Dashboard />, permission: 'am_station.read' };
    const amAnalytics: NavItem = { label: 'Deep Analytics', path: '/asset-management/analytics', icon: <QueryStats />, permission: 'am_station.read' };
    return NAV.map((section) => {
      if (section.heading === 'Overview') {
        return { ...section, items: [amDashboard, amAnalytics, ...section.items.filter((i) => i.path !== '/dashboard')] };
      }
      if (section.heading === 'Asset Management') {
        return { ...section, items: section.items.filter((i) => i.path !== '/asset-management/dashboard' && i.path !== '/asset-management/analytics') };
      }
      if (section.heading === 'Home') {
        return { ...section, items: section.items.filter((i) => i.path !== '/notifications') };
      }
      return section;
    });
  }, [isPortal, portalType, clientHasAm]);

  // White-label branding — dynamically loads the active tenant's identity.
  const branding = useQuery({ queryKey: ['tenant', 'me'], queryFn: () => brandingApi.me(), retry: false, staleTime: 5 * 60 * 1000 });
  useEffect(() => {
    const b = branding.data;
    if (!b) return;
    document.title = b.companyName || b.name || 'MyevCRM';
    if (b.primaryColor) document.documentElement.style.setProperty('--brand-primary', b.primaryColor);
    if (b.faviconUrl) {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = b.faviconUrl;
    }
  }, [branding.data]);
  const brandName = branding.data && !branding.data.isPlatform ? branding.data.name : 'MyevCRM';

  // Mini (icon-only) sidebar applies on desktop only.
  const mini = collapsed && !isMobile;
  const width = mini ? DRAWER_MINI : DRAWER_WIDTH;
  const go = (path: string) => { navigate(path); setMobileOpen(false); };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: mini ? 'center' : 'flex-start', gap: 1.25, px: mini ? 0 : 2.5, height: 64, flexShrink: 0 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Bolt fontSize="small" />
        </Box>
        {!mini && (
          <Box lineHeight={1}>
            <Typography fontWeight={800} fontSize={15} color="primary.dark" noWrap>{brandName}</Typography>
            <Typography fontSize={11} color="text.secondary" letterSpacing={1} textTransform="uppercase">EV CRM</Typography>
          </Box>
        )}
      </Box>
      <Divider />
      <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, py: 1 }}>
        {navSections.map((section, sectionIdx) => {
          if (section.verticals) {
            // Hide sections whose verticals are none of them live (un-launched).
            if (!section.verticals.some((c) => liveVerticalCodes.has(c))) return null;
            // Hide vertical-tagged sections that don't match the active vertical.
            if (effectiveVertical && !section.verticals.includes(effectiveVertical.code)) return null;
          } else if (effectiveVertical && !section.alwaysShow) {
            // Untagged platform section: hidden while a specific vertical is active
            // (each vertical is its own mini-CRM). Shown only in "All Verticals".
            return null;
          }
          // Explicitly hide a section in specific verticals (e.g. AM approvals shouldn't show in EPC).
          if (section.excludeVerticals && effectiveVertical && section.excludeVerticals.includes(effectiveVertical.code)) return null;
          const items = section.items.filter((i) => !i.permission || hasPermission(i.permission));
          if (items.length === 0) return null;
          const open = mini ? true : (openGroups[section.heading] ?? sectionHasActive(items));
          return (
            <List key={`${section.heading}-${sectionIdx}`} dense disablePadding sx={{ pb: 0.25 }}>
              {mini ? (
                <Box sx={{ height: 8 }} />
              ) : (
                <ListItemButton onClick={() => toggleGroup(section.heading)} sx={{ py: 0.25, px: 2, borderRadius: 1, mx: 1 }}>
                  <ListItemText primary={section.heading} primaryTypographyProps={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary' }} />
                  {open ? <ExpandLess sx={{ fontSize: 16, color: 'text.disabled' }} /> : <ExpandMore sx={{ fontSize: 16, color: 'text.disabled' }} />}
                </ListItemButton>
              )}
              <Collapse in={open} timeout="auto">
                {items.map((item) => {
                  const selected = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                  const button = (
                    <ListItemButton
                      key={item.path}
                      selected={selected}
                      onClick={() => go(item.path)}
                      sx={{ py: 0.4, justifyContent: mini ? 'center' : 'flex-start', px: mini ? 1 : 2, borderRadius: 1, mx: mini ? 0.5 : 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: mini ? 0 : 32, justifyContent: 'center', '& svg': { fontSize: 20 } }}>{item.icon}</ListItemIcon>
                      {!mini && <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 13, fontWeight: selected ? 600 : 500 }} />}
                    </ListItemButton>
                  );
                  return mini ? (
                    <Tooltip key={item.path} title={item.label} placement="right">{button}</Tooltip>
                  ) : button;
                })}
              </Collapse>
            </List>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" onClick={() => (isMobile ? setMobileOpen(true) : setCollapsed((c) => !c))}>
            {isMobile ? <MenuIcon /> : mini ? <MenuIcon /> : <MenuOpen />}
          </IconButton>
          <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
            <Breadcrumbs />
          </Box>
          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />
          <VerticalSwitcher />
          {user?.roles?.[0] && (
            <Chip
              size="small"
              label={user.roles[0].replace(/_/g, ' ')}
              sx={{ mr: 0.5, textTransform: 'capitalize', bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.dark', display: { xs: 'none', sm: 'inline-flex' } }}
            />
          )}
          <ColorModeToggle />
          <NotificationMenu />
          <Tooltip title="Account">
            <IconButton onClick={(e) => setAnchor(e.currentTarget)} size="small">
              <Avatar sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>{initials(user?.fullName)}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
              <Typography fontWeight={600} noWrap>{user?.fullName}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{user?.email}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setAnchor(null); setPwOpen(true); }}>
              <ListItemIcon><VpnKey fontSize="small" /></ListItemIcon>
              Change Password
            </MenuItem>
            <MenuItem onClick={() => { setAnchor(null); void logout(); }}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{ width, flexShrink: 0, whiteSpace: 'nowrap', '& .MuiDrawer-paper': { width, boxSizing: 'border-box', overflowX: 'hidden', transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }) } }}
        >
          <Toolbar sx={{ minHeight: '0 !important', height: 0, p: 0, m: 0, visibility: 'hidden' }} />
          {drawer}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, width: { md: `calc(100% - ${width}px)` }, p: { xs: 2, md: 3 } }}>
        <Toolbar />
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
          <Outlet />
        </Box>
      </Box>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
      <ProfileCompletionGate />
    </Box>
  );
}
